import { Record, List, Map } from 'immutable';
import moment from 'moment';
import _ from 'lodash';

import config from '../../config';
import { 
    FavouriteExcludedTicketTypes,
    OPEN_GOLF_TICKET_CODE_REGEX,
    SmartCarnetValidation,
    ReservableStates,
    TPE_GROUP_SAVE_CODE 
} from '../../constants/AppConstants';
import Route from '../Route';
import Station from '../Station';
import TicketType from '../TicketType';
import OrderTicket from './Ticket';
import OrderJourney from './Journey';
import RailcardTickets from './RailcardTickets';
import PassengerRailcard from '../PassengerRailcard';
import { resolve, resolveAll } from '../../helpers/LinkMapper';
import { sum, compact, unique } from '../../helpers/Immutable';
import { matchRailcards } from '../../helpers/Railcards';

const defaults = {
    id: null,
    fareId: null,
    origin: new Station(),
    destination: new Station(),
    route: new Route(),
    ticketType: new TicketType(),
    smartTicketType: null,
    crossLondon: false,
    restrictionCode: null,
    outwardJourney: new OrderJourney(),
    returnJourney: new OrderJourney(),
    tickets: new List()
};

class OrderFare extends Record(defaults) { // eslint-disable-line new-cap
    static fromJSON (json = {}) {
        return new OrderFare({
            id: json.id,
            fareId: json.fareId,
            origin: resolve(json.origin).as(Station),
            destination: resolve(json.destination).as(Station),
            route: resolve(json.routeCode || json.route).as(Route),
            ticketType: resolve(json.ticketTypeCode || json.ticketType).as(TicketType),
            smartTicketType: resolve(json.smartTicketTypeCode).with(String),
            crossLondon: resolve(json.crossLondon).with(Boolean),
            restrictionCode: resolve(json.restrictionCode).with(String),
            outwardJourney: resolve(json.outwardJourney).as(OrderJourney),
            returnJourney: resolve(json.returnJourney).as(OrderJourney),
            tickets: resolveAll(json.tickets).as(OrderTicket)
        });
    }

    getName () { return this.ticketType.name; }

    getDescription () { return this.route.description.length > 1 ? this.route.description : null; }

    isReturn () { return this.ticketType.isReturn; }

    isGroupSave () { return this.tickets.some((ticket) => ticket.isGroupSave()); }

    isDaySave () { return this.ticketType.isDaySave(); }

    isDaySaveOffPeak () { return this.ticketType.isDaySaveOffPeak(); }

    isRoversRangers () { return this.ticketType.isRoversRangers(); }

    isCarnet () { return this.ticketType.isCarnet; }

    isPlusBus () { return this.ticketType.isPlusBus(); }

    isSeason () { return this.ticketType.isSeason; }

    isCrossLondon () { return this.crossLondon; }

    isFlexi () { return this.ticketType.isFlexi; }

    isAdvance () { return this.ticketType.isAdvance; }

    isOpenGolf () { return this.ticketType && this.ticketType.code && this.ticketType.code.match(OPEN_GOLF_TICKET_CODE_REGEX); }

    isSmartcard () {
        return this.tickets.some((ticket) => ticket.getIn(['passenger', 'smartcardNumber']));
    }

    isSmartCarnet () {
        return this.ticketType.isSmartCarnet();
    }

    isStandard () { return !this.ticketType.isFirstClass; }

    isFirstClass () { return this.ticketType.isFirstClass; }

    adultTickets () {
        return this.tickets.filter((ticket) => ticket.adults);
    }

    adults () {
        return this.isSmartCarnet() ? 1 : sum(this.adultTickets(), 'adults');
    }

    childTickets () {
        return this.tickets.filter((ticket) => ticket.children);
    }

    children () {
        return sum(this.childTickets(), 'children');
    }

    getAdultTotalPrice () {
        return this.getTotalPrice(this.adultTickets());
    }

    getChildTotalPrice() {
        return this.getTotalPrice(this.childTickets());
    }

    getTotalPrice (tickets = this.tickets) {
        return sum(tickets, 'price');
    }

    getMatchingTicketPrice (price) {
        const sortedTickets = this.tickets.sortBy(x => x.price);
        const match = sortedTickets.find(ticket => ticket.price >= price);
        return match && match.price;
    }

    getRailcards (tickets = this.tickets) {
        const railcards = compact(tickets.map((t) => t.railcard));
        return unique(railcards, (r) => r.code);
    }

    getRefundableTickets () {
        return this.tickets.filter((ticket) => ticket.isRefundable);
    }

    getTicketsWithRailCard (railcard, tickets = this.tickets) {
        const railcardTickets = tickets.filter((ticket) => ticket.hasRailcard(railcard));
        return new RailcardTickets().concat(railcardTickets);
    }

    getPlusBusTickets () {
        return this.isPlusBus() ? this.tickets : new List();
    }

    getRailcardTickets () {
        return this.getRailcards().map((railcard) => this.getTicketsWithRailCard(railcard));
    }

    getRailcardCounts (property = 'code') {
        return this.tickets
            .filter((railcardTicket) => railcardTicket.railcard && railcardTicket.railcard[property] && !railcardTicket.railcard.isGroupSave())
            .reduce((counts, ticket) => counts.update(ticket.railcard[property], 0, quantity => quantity + 1), new Map());
    }

    getPassengerRailcards() {
        return this.getRailcardCounts().map((quantity, code) => new PassengerRailcard({ code, quantity })).toList();
    }

    getAllRailcards() {
        return this.tickets.reduce((railcards, { railcard }) => {
            if (railcard && railcard.code && !railcard.isGroupSave()) {
                railcards = railcards.push(railcard);
            }
            return railcards;
        }, new List());
    }

    getPhotocardNumber () {
        if (!this.isSeason()) { return null; }

        const passenger = this.getPassengers().first();
        if (!passenger) { return null; }

        return passenger.get('photocardNumber');
    }

    hasPhotocardNumber () {
        return !!this.getPhotocardNumber();
    }

    getValidTo () {
        const journey = this.returnJourney || this.outwardJourney;
        return journey.validTo;
    }

    getValidFrom () {
        return this.outwardJourney.validFrom;
    }

    getOutwardValidityPeriod () {
        return this.outwardJourney.getValidityPeriod();
    }

    getDepartureTime () {
        return this.outwardJourney.getDepartureTime();
    }

    isPrinted () {
        return this.tickets.every((ticket) => ticket.printed);
    }

    isUnprinted () {
        return this.tickets.some((ticket) => !ticket.printed);
    }

    getCarnetInformation () {
        return this.ticketType.carnetInformation;
    }


    getFlexiInformation () {
        return this.ticketType.flexiInformation;
    }

    hasRefundableTickets () {
        return this.tickets.some((ticket) => ticket.isRefundable);
    }

    hasAmendableTickets () {
        return this.tickets.some((ticket) => ticket.canAmend());
    }

    hasRefundedTickets () {
        return this.tickets.some((ticket) => ticket.isRefunded);
    }

    isReorderable () {
        return !(this.isPlusBus() || this.isAdvance() || this.isDaySave() || this.isRoversRangers() || this.isOpenGolf());
    }

    getPassengers () {
        return compact(this.tickets.map((ticket) => ticket.passenger));
    }

    hasRailcard () {
        return this.tickets.some((ticket) => ticket.railcard && !ticket.isGroupSave());
    }

    hasTicket (id) {
        return this.tickets.some(ticket => ticket.id === id);
    }

    isTravelcard () { return this.origin.isTravelcard() || this.destination.isTravelcard(); }

    isExcludedFromFavourites () {
        return this.ticketType && FavouriteExcludedTicketTypes.some(excludedCode => this.ticketType.code && this.ticketType.code.match(excludedCode));
    }

    matchFare (fare) {
        return this.origin.nlc === fare.origin.nlc &&
            this.destination.nlc === fare.destination.nlc &&
            this.adults() === fare.adults() &&
            this.children() === fare.children() &&
            this.route.code === fare.route.code &&
            this.ticketType.code === fare.ticketType.code &&
            this.isSeason() === fare.isSeason() &&
            this.isReturn() === fare.isReturn() &&
            (this.ticketType.isSeason ? this.getOutwardValidityDurationString() === fare.getOutwardValidityDurationString() : true) &&
            matchRailcards(this, fare);
    }

    getOutwardValidityDurationString () {
       return this.getOutwardValidityPeriod().toDuration().toJSON();
    }

    getNRELinkParams () {
        let fareClass = 'Standard';
        if (this.isFirstClass()) {
            fareClass = 'First';
        }
        return {
            origin: this.origin.crs || this.origin.nlc,
            destination: this.destination.crs || this.destination.nlc,
            outwardDate: moment(this.outwardJourney.validFrom._d).format('DD-MMM-YY'),
            fareClass
        };
    }

    getNRELinkQuery () {
        let query = {};
        if (this.isReturn() && this.returnJourney && !!this.returnJourney.legs.size) {
            _.set(query, 'returnDate', moment(this.returnJourney.validFrom._d).format('DD-MMM-YY'));
        }
        return query;
    }

    hasDisabledPeopleRailcard () {
        return this.tickets.some((ticket) => ticket.hasDisabledPeopleRailcard());
    }

    getNumberOfPasses () {
        if (this.isSmartCarnet()) {
            return this.tickets.size * SmartCarnetValidation.PASSES_PER_BOOK;
        }

        if (this.isFlexi()) {
            return this.ticketType.flexiInformation.numberOfTickets;
        }

        return null;
    }

    hasRailcard1617 () {
        return this.tickets.some((ticket) => ticket.hasRailcard1617());
    }

    hasRailcardJCP () {
        return this.tickets.some((ticket) => ticket.hasRailcardJCP());
    }

    hasCountedPlaceWarning() {
        const tocs = config.get('showCountedPlaceWarnings');
        if (!tocs || tocs.length === 0) {
            return false;
        }

        const legs = []
            .concat(this.outwardJourney ? this.outwardJourney.legs.toArray() : [])
            .concat(this.returnJourney ? this.returnJourney.legs.toArray() : []);
        return legs.some(
            leg =>
                leg.reservable === ReservableStates.COMPULSORY &&
                leg.hasCountedPlaceSeats() &&
                tocs.includes(leg.getIn(['toc', 'code'])),
        );
    }

    isTPEGroupSave () {
        return this.discountCode === TPE_GROUP_SAVE_CODE;
    }
}

export default OrderFare;



// WEBPACK FOOTER //
// ./src/records/order/Fare.js